import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { cn } from '../../../lib/utils';

/* ------------------------------------------------------------------ */
/*  Root / Trigger / Close / Portal — re-export Radix Dialog parts    */
/* ------------------------------------------------------------------ */

export const CharlieSheet = DialogPrimitive.Root;
export const CharlieSheetTrigger = DialogPrimitive.Trigger;
export const CharlieSheetClose = DialogPrimitive.Close;
export const CharlieSheetPortal = DialogPrimitive.Portal;

/* ------------------------------------------------------------------ */
/*  Overlay                                                           */
/* ------------------------------------------------------------------ */

export type CharlieSheetOverlayProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Overlay
>;

export function CharlieSheetOverlay({
  className,
  ...props
}: CharlieSheetOverlayProps) {
  return (
    <DialogPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-black/40',
        'data-[state=open]:animate-in data-[state=open]:fade-in-0',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
        className,
      )}
      {...props}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Content                                                           */
/* ------------------------------------------------------------------ */

const sheetContentVariants = cva(
  'fixed z-50 flex flex-col bg-background shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:duration-300 data-[state=closed]:duration-200',
  {
    variants: {
      side: {
        top: 'inset-x-0 top-0 border-b data-[state=open]:slide-in-from-top data-[state=closed]:slide-out-to-top',
        bottom:
          'inset-x-0 bottom-0 border-t data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
        left: 'inset-y-0 left-0 h-full w-3/4 max-w-sm border-r data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left',
        right:
          'inset-y-0 right-0 h-full w-3/4 max-w-sm border-l data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
      },
    },
    defaultVariants: {
      side: 'left',
    },
  },
);

export type CharlieSheetContentVariants = VariantProps<
  typeof sheetContentVariants
>;

export interface CharlieSheetContentProps
  extends
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    CharlieSheetContentVariants {
  /** Hide the built-in close (X) button. Default: `false`. */
  hideClose?: boolean;
}

/**
 * CharlieSheet — A slide-in panel (drawer) for overlaying content.
 *
 * Built on top of [Radix Dialog](https://www.radix-ui.com/primitives/docs/components/dialog)
 * and styled to match the [ShadCN Sheet](https://ui.shadcn.com/docs/components/sheet).
 *
 * ## Usage
 *
 * ```tsx
 * import {
 *   CharlieSheet,
 *   CharlieSheetTrigger,
 *   CharlieSheetContent,
 * } from '@ver.id/libs.ui.charlie';
 *
 * // Basic left-side drawer
 * <CharlieSheet>
 *   <CharlieSheetTrigger asChild>
 *     <button>Open menu</button>
 *   </CharlieSheetTrigger>
 *   <CharlieSheetContent side="left">
 *     <p>Drawer content here</p>
 *   </CharlieSheetContent>
 * </CharlieSheet>
 *
 * // Controlled usage
 * const [open, setOpen] = useState(false);
 * <CharlieSheet open={open} onOpenChange={setOpen}>
 *   <CharlieSheetContent side="right" hideClose>
 *     <p>Content</p>
 *     <button onClick={() => setOpen(false)}>Close</button>
 *   </CharlieSheetContent>
 * </CharlieSheet>
 * ```
 *
 * ## Gotchas
 *
 * - **Portal**: Content renders inside a portal by default — it appears
 *   above everything else regardless of DOM position.
 * - **Body scroll lock**: Radix Dialog locks body scroll when open.
 *   This is intentional for mobile drawers.
 * - **Side prop**: Determines slide direction. Use `"left"` for navigation
 *   drawers, `"right"` for detail panels, `"bottom"` for mobile action sheets.
 * - **Close button**: A close (X) button is rendered by default in the
 *   top-right corner. Pass `hideClose` to remove it (e.g. when you have
 *   your own close button in a custom header).
 */
export function CharlieSheetContent({
  side = 'left',
  hideClose = false,
  className,
  children,
  ...props
}: CharlieSheetContentProps) {
  return (
    <CharlieSheetPortal>
      <CharlieSheetOverlay />
      <DialogPrimitive.Content
        data-slot="sheet-content"
        className={cn(sheetContentVariants({ side }), className)}
        {...props}
      >
        {children}
        {!hideClose && (
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
            <FontAwesomeIcon icon={faXmark} className="size-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </CharlieSheetPortal>
  );
}
